import { DateTime } from '@ksyos/date-time';
import { Alert, Box, FormGroup, Radio, RadioButton, RadioGroup, Text, TextInput } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { ICheckedTreatment } from '@common/interfaces/checkedTreatment';
import type { OsasAddendumRecord } from '@common/validation/addendum';
import type { IServiceConsumer } from '@frontend/components/interfaces/serviceConsumer';
import { MraAbcdScore } from '@common/enums/mraAbcdScore';
import { ProcessEntity } from '@common/enums/processEntity';
import { contactFileExtensions } from '@common/utils/fileExtensions';
import {
    OsasvcV2CpapOption,
    OsasvcV2CTherapieGestartOption,
    OsasvcV2TherapieNamelijkOption,
    positieafhankelijkOptions,
} from '@common/validation/customControls/somnology';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import { isEmailDuplicateForPatient } from '@frontend/services/api/therapielandApiService';
import { fetchJson } from '@frontend/store/fetch';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { RequestStatus } from '../../../../../../../common/enums/requestStatus';
import type { ICustomControlRequestAddEditProps } from '../../../interfaces/';
import { getServiceLabel } from '../../../../../../../common/customControls/getServiceLabel';
import { EpisodeCategory } from '../../../../../../../common/enums/episodeCategory';
import { FilesUrlBase } from '../../../../../../../common/enums/filesUrlBase';
import { OsasQuestionnaireName } from '../../../../../../../common/enums/osasQuestionnaireName';
import { ServiceCode } from '../../../../../../../common/enums/serviceCode';
import { ToastType } from '../../../../../../../common/enums/toastType';
import { getOsasQuestionnaireScore } from '../../../../../../../common/utils/osasQuestionnaireUtils';
import { translateKey } from '../../../../../../translate';
import { canCreateOsasvc } from '../../../../../../utils/canCreateOsasvc';
import { fetchActiveOsasqResendDate } from '../../../../../../utils/fetchActiveOsasqResendDate';
import { useAbortableFetch } from '../../../../../../utils/hooks/useAbortableFetch';
import { useEpisodeContext } from '../../../../../../utils/hooks/useEpisodeContext';
import { showServiceToast } from '../../../../../../utils/showToast';
import { CheckMarkButton } from '../../../../../elements/buttons/checkMarkButton/CheckMarkButton';
import { SaveButton } from '../../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../../elements/carePathButtons/SaveSendButton';
import {
    Button,
    ButtonContainer,
    Feedback,
    Form,
    FormParagraph,
    HeaderDivider,
    IconOnClick,
    InputCheckbox,
    NullableInputText,
} from '../../../../../elements/common';
import { FormContextProvider } from '../../../../../elements/formContext';
import { FormLabel } from '../../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../../elements/forms/InputContainer';
import { InputTextCheckbox } from '../../../../../elements/InputTextCheckbox';
import { Modal } from '../../../../../elements/modals/Modal';
import { TherapielandProgrammasModal } from '../../../../../elements/TherapielandProgrammasModal';
import { CheckboxInput } from '../../../../../fields/Checkbox';
import { DateField, DateInput } from '../../../../../fields/Date';
import { NumberInput } from '../../../../../fields/Number';
import { RadioGroupField } from '../../../../../fields/RadioGroup';
import { TextField } from '../../../../../fields/Text';
import { TextAreaField } from '../../../../../fields/TextArea';
import { ReloadEpisode } from '../../../../episodes/reloadEpisode';
import { AssetDropzoneWithActions } from '../../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../../utils/v1/CareProviderSelector';
import { NewOrEditRequestProcessContainer } from '../../../utils/v1/NewOrEditRequestProcessContainer';
import { OsasvcV2QuestionnairesScoresRead } from './OsasvcV2QuestionnairesScoresRead';

interface IAanvullendBeleid {
    advies_lifestyle: boolean;
    advies_lifestyle_namelijk: string | null;
    anders: string | null;
}

export interface IToesturenTherapielandModules {
    bewegen: boolean;
    leefstijl: boolean;
    lekker_slapen: boolean;
    ontspanning: boolean;
    piekeren: boolean;
    slaap_en_shifts: boolean;
    slaaprestrictie: boolean;
}

interface IVervolgAfspraakMetSpecialist {
    anders: string | null;
    volgens_protocol: boolean;
}

export interface IOsasvcV2CustomControlData {
    aanvullend_beleid: IAanvullendBeleid;
    addendum?: OsasAddendumRecord;
    advies_aan_de_huisarts: string | null;
    afwijkendHartritme: boolean;
    afwijkendHartritmeRemarks: string | null;
    anamnese: string | null;
    betreft_proefperiode: boolean | null;
    checkedTreatments?: ICheckedTreatment[];
    conclusie: string | null;
    cpap_aangepaste_instellingen_namelijk: OsasvcV2CpapOption | null;
    cpap_instellingen: string | null;
    date_contact: string | null;
    emailAddress: string | null;
    gemiddeldeAhi: string | null;
    gemiddeldeOdi: string | null;
    gemiddeldeSaturatie: string | null;
    inefficienteSlaaphygiene: boolean;
    inefficienteSlaaphygieneRemarks: string | null;
    laagstGemetenSaturatie: string | null;
    nieuw_slaaponderzoek: boolean;
    nieuw_slaaponderzoek_end_date: string | null;
    nieuw_slaaponderzoek_start_date: string | null;
    nieuwe_therapie_starten: boolean | null;
    nieuwe_therapie_starten_bipap_instellingen: string | null;
    nieuwe_therapie_starten_mra_abcd_score: MraAbcdScore | null;
    nieuwe_therapie_starten_namelijk: OsasvcV2TherapieNamelijkOption | null;
    nieuwe_therapie_starten_toelichting: string | null;
    odi4: string | null;
    onderzoek: string | null;
    osasq_resend_date: string | null;
    osasq_resend_week_dif: number | null;
    overigeRelevanteBevindingenAndersText: string | null;
    positieafhankelijk: string | null;
    positieafhankelijkRemarks: string | null;
    questionnaires: IOsasvcQuestionnaire[];
    relevanteHypoxemie: boolean | null;
    relevanteHypoxemieRemarks: string | null;
    slechteSlaapefficientie: boolean;
    therapie: string | null;
    therapie_gestart: boolean | null;
    therapie_gestart_namelijk: OsasvcV2TherapieNamelijkOption | null;
    toelichting: string | null;
    toesturen_therapieland_modules?: IToesturenTherapielandModules;
    veelBewegingen: boolean;
    vervolg_afspraak_met_specialist: IVervolgAfspraakMetSpecialist;
    vervolg_afspraak_met_specialist_nieuw_slaaponderzoek: string | null;
}

export interface IOsasvcQuestionnaire {
    dateSent: string;
    name: OsasQuestionnaireName;
    score: number;
}

export function OsasvcV2RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const abortSignal = useAbortSignal();
    const navigate = useNavigate();
    const [buttonDisabled, setButtonDisabled] = useState<boolean | undefined>();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [modalMessage, setModalMessage] = useState<string>();
    const [canCreateRequestOsasvc, setCanCreateRequestOsasvc] = useState<boolean>(false);
    const [activeOsasqResendDate, setActiveOsasqResendDate] = useState<Date | null>(null);
    const [therapielandProgrammaIsModalShown, setTherapielandProgrammaIsModalShown] = useState<boolean>(false);
    const [showEmailAddressError, setShowEmailAddressError] = useState<boolean>(false);
    const [hasActiveGgzbTherServiceConsumerRights, setHasActiveGgzbTherServiceConsumerRights] = useState<boolean>(true);
    const { patient } = useEpisodeContext();

    // Adjusting vertical alignment of radioGroups compared to the other form inputs,
    // this is needed because it's now using the new FormGroup instead of the old HorizontalFormGroup
    const radioGroupStyles = {
        marginLeft: '-6px',
    };

    const abortableFetch = useAbortableFetch();

    useEffect(() => {
        initializeMissingCustomControlDataFields();
        setButtonDisabled(true);
        checkForCompletedRequest();
        loadPatientQuestionnaires();
        loadPatientEmail();
        isServiceConsumerOfGGZTherapieland();
        checkHasActiveOsasqRequest();
    }, []);

    const customControlData: IOsasvcV2CustomControlData = props.request.customControlData;

    return (
        <NewOrEditRequestProcessContainer
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={getServiceLabel(props.service.code, 'request', props.service.name)}
        >
            <Form onSubmit={async () => null}>
                <CareProviderSelector
                    {...props}
                    entity={props.request}
                    hideCorrectGpWarning={true}
                />

                <FormContextProvider
                    value={{
                        placeholderGroupName: 'procedure',
                        item: customControlData,
                        errors: props.errors,
                        customControlData: true,
                        onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await props.validateOneCustomControl(key),
                    }}
                >
                    <DateField
                        error={props.errors.get('customControlData.date_contact')}
                        fieldKey="date_contact"
                        hasErrors={props.errors.has('customControlData.date_contact')}
                        isRequired
                        label={translateKey('request.dateContact')}
                        maxDate={DateTime.now().format()}
                        onChange={async (value) => {
                            await props.handleChangeCustomControl({ date_contact: value });
                            await props.validateOneCustomControl('date_contact');
                        }}
                        placeholder={`${translateKey('common.date')}...`}
                        size="medium"
                    />

                    <TextAreaField
                        error={props.errors.get('customControlData.anamnese')}
                        fieldKey="anamnese"
                        isRequired
                        label={translateKey('carePlan.anamnese')}
                        placeholder="Vul hier de anamnese in..."
                    />

                    <TextAreaField
                        error={props.errors.get('customControlData.onderzoek')}
                        fieldKey="onderzoek"
                        label={translateKey('procedure.procedure')}
                        placeholder="Omschrijf je onderzoeksresultaten..."
                    />

                    <OsasvcV2QuestionnairesScoresRead questionnaires={customControlData.questionnaires} />

                    <FormGroup
                        hasErrors={props.errors.has('customControlData.nieuw_slaaponderzoek')}
                        label="Nieuw slaaponderzoek"
                    >
                        <RadioGroup
                            layout="row"
                            name="nieuw_slaaponderzoek"
                            onChange={async (event) => {
                                await handleChangeNieuwSlaaponderzoek(event.target.value === 'true');
                                await props.validateOneCustomControl('nieuw_slaaponderzoek');
                            }}
                            sx={radioGroupStyles}
                            value={customControlData.nieuw_slaaponderzoek?.toString() ?? ''}
                        >
                            <RadioButton
                                label="Ja"
                                value="true"
                            />
                            <RadioButton
                                label="Nee"
                                value="false"
                            />
                        </RadioGroup>
                    </FormGroup>

                    {customControlData.nieuw_slaaponderzoek && (
                        <>
                            <FormGroup label="Periode slaaponderzoek">
                                <Box
                                    display="flex"
                                    gap={2}
                                >
                                    <Box>
                                        <DateInput
                                            error={props.errors.get('nieuw_slaaponderzoek_start_date')}
                                            hasMaxWidth={true}
                                            onChange={async (value) =>
                                                await props.handleChangeCustomControl({
                                                    nieuw_slaaponderzoek_start_date: value,
                                                    nieuw_slaaponderzoek_end_date: null,
                                                })
                                            }
                                            placeholder="Datum..."
                                            value={customControlData.nieuw_slaaponderzoek_start_date}
                                        />
                                    </Box>
                                    <Text
                                        as="small"
                                        mt={2.5}
                                        size="xs"
                                        sx={{
                                            display: 'block',
                                            height: '40px',
                                            width: '100%',
                                        }}
                                        textAlign="center"
                                    >
                                        {translateKey('common.to')}
                                    </Text>
                                    <Box>
                                        <DateInput
                                            error={getValidationErrorsForNieuwSlaaponderzoekDates()}
                                            hasMaxWidth={true}
                                            minDate={customControlData.nieuw_slaaponderzoek_start_date}
                                            onChange={async (value) =>
                                                await props.handleChangeCustomControl({
                                                    nieuw_slaaponderzoek_end_date: value,
                                                })
                                            }
                                            placeholder={translateKey('common.dateFormat')}
                                            value={customControlData.nieuw_slaaponderzoek_end_date}
                                        />
                                    </Box>
                                </Box>
                            </FormGroup>
                            <TextField
                                automation="gemiddeldeAhi"
                                error={props.errors.get('customControlData.gemiddeldeAhi')}
                                fieldKey="gemiddeldeAhi"
                                hasErrors={props.errors.has('customControlData.gemiddeldeAhi')}
                                isRequired={true}
                                label="Gemiddelde AHI"
                                placeholder=""
                                size="small"
                            />

                            <TextField
                                automation="gemiddeldeOdi"
                                error={props.errors.get('customControlData.gemiddeldeOdi')}
                                fieldKey="gemiddeldeOdi"
                                hasErrors={props.errors.has('customControlData.gemiddeldeOdi')}
                                isRequired={true}
                                label="Gemiddelde ODI 3%"
                                placeholder=""
                                size="small"
                            />

                            <TextField
                                error={props.errors.get('customControlData.odi4')}
                                fieldKey="odi4"
                                hasErrors={props.errors.has('customControlData.odi4')}
                                isRequired={true}
                                label="Gemiddelde ODI 4%"
                                placeholder=""
                                size="small"
                            />

                            <TextField
                                error={props.errors.get('customControlData.gemiddeldeSaturatie')}
                                fieldKey="gemiddeldeSaturatie"
                                hasErrors={props.errors.has('customControlData.gemiddeldeSaturatie')}
                                isRequired={true}
                                label="Gemiddelde saturatie %"
                                placeholder=""
                                size="small"
                            />

                            <TextField
                                error={props.errors.get('customControlData.laagstGemetenSaturatie')}
                                fieldKey="laagstGemetenSaturatie"
                                hasErrors={props.errors.has('customControlData.laagstGemetenSaturatie')}
                                isRequired={true}
                                label="Laagst gemeten saturatie %"
                                placeholder=""
                                size="small"
                            />

                            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.relevanteHypoxemie')}>
                                <FormLabel isRequired>Relevante Hypoxemie</FormLabel>
                                <RadioGroup
                                    layout="row"
                                    name="relevanteHypoxemie"
                                    onChange={async (e) => {
                                        const update: Partial<IOsasvcV2CustomControlData> = {};
                                        update.relevanteHypoxemie = e.target.value === 'true';
                                        if (e.target.value !== 'true') {
                                            update.relevanteHypoxemieRemarks = null;
                                        }
                                        await props.handleChangeCustomControl(update);
                                    }}
                                    value={customControlData.relevanteHypoxemie?.toString() ?? ''}
                                >
                                    <RadioButton
                                        label="Ja"
                                        value="true"
                                    />
                                    <RadioButton
                                        label="Nee"
                                        value="false"
                                    />
                                </RadioGroup>
                            </HorizontalFormGroup>

                            {customControlData.relevanteHypoxemie && (
                                <TextAreaField
                                    error={props.errors.get('customControlData.relevanteHypoxemieRemarks')}
                                    fieldKey="relevanteHypoxemieRemarks"
                                    hasErrors={props.errors.has('customControlData.relevanteHypoxemieRemarks')}
                                    label={translateKey('common.remarks')}
                                    placeholder={translateKey('common.remarksOrQuestions')}
                                />
                            )}

                            <RadioGroupField
                                automation="positieafhankelijk"
                                fieldKey="positieafhankelijk"
                                hasErrors={props.errors.has('customControlData.positieafhankelijk')}
                                isRequired={true}
                                label="Positieafhankelijk"
                                onChange={async (value) => {
                                    const update: Partial<IOsasvcV2CustomControlData> = {};
                                    update.positieafhankelijk = value;
                                    if (value !== 'Ja') {
                                        update.positieafhankelijkRemarks = null;
                                    }
                                    await props.handleChangeCustomControl(update);
                                }}
                                options={positieafhankelijkOptions}
                            />

                            {customControlData.positieafhankelijk === 'Ja' && (
                                <TextAreaField
                                    error={props.errors.get('customControlData.positieafhankelijkRemarks')}
                                    fieldKey="positieafhankelijkRemarks"
                                    hasErrors={props.errors.has('customControlData.positieafhankelijkRemarks')}
                                    label={translateKey('common.remarks')}
                                    placeholder={translateKey('common.remarksOrQuestions')}
                                />
                            )}

                            <HorizontalFormGroup
                                hasErrors={
                                    props.errors.has('customControlData.veelBewegingen') ||
                                    props.errors.has('customControlData.slechteSlaapefficientie') ||
                                    props.errors.has('customControlData.afwijkendHartritme') ||
                                    props.errors.has('customControlData.afwijkendHartritmeRemarks') ||
                                    props.errors.has('customControlData.overigeRelevanteBevindingenAndersText') ||
                                    props.errors.has('customControlData.overigeRelevanteBevindingen')
                                }
                            >
                                <FormLabel>Overige relevante bevindingen</FormLabel>
                                <InputContainer>
                                    <InputCheckbox
                                        automation="inefficienteSlaaphygiene"
                                        checked={customControlData.inefficienteSlaaphygiene}
                                        error={props.errors.get('customControlData.inefficienteSlaaphygiene')}
                                        onChange={async (value) => {
                                            const update: Partial<IOsasvcV2CustomControlData> = {};
                                            update.inefficienteSlaaphygiene = value;
                                            if (value !== true) {
                                                update.inefficienteSlaaphygieneRemarks = null;
                                            }
                                            await props.handleChangeCustomControl(update);
                                        }}
                                        onDoValidate={async () => {
                                            await props.validateOneCustomControl('inefficienteSlaaphygiene');
                                        }}
                                        text="Inefficiënte slaaphygiëne"
                                    />
                                    {customControlData.inefficienteSlaaphygiene && (
                                        <NullableInputText
                                            automation="inefficienteSlaaphygieneRemarks"
                                            error={props.errors.get(
                                                'customControlData.inefficienteSlaaphygieneRemarks'
                                            )}
                                            onChange={async (value) =>
                                                await props.handleChangeCustomControl({
                                                    inefficienteSlaaphygieneRemarks: value,
                                                })
                                            }
                                            onDoValidate={async () =>
                                                await props.validateOneCustomControl('inefficienteSlaaphygieneRemarks')
                                            }
                                            placeholder="Vanwege..."
                                            value={customControlData.inefficienteSlaaphygieneRemarks}
                                        />
                                    )}
                                    <InputCheckbox
                                        automation="veelBewegingen"
                                        checked={customControlData.veelBewegingen}
                                        error={props.errors.get('customControlData.veelBewegingen')}
                                        onChange={async (value) =>
                                            await props.handleChangeCustomControl({ veelBewegingen: value })
                                        }
                                        onDoValidate={async () => {
                                            await props.validateOneCustomControl('veelBewegingen');
                                        }}
                                        text="Veel bewegingen"
                                    />
                                    <InputCheckbox
                                        automation="slechteSlaapefficientie"
                                        checked={customControlData.slechteSlaapefficientie}
                                        error={props.errors.get('customControlData.slechteSlaapefficientie')}
                                        onChange={async (value) =>
                                            await props.handleChangeCustomControl({ slechteSlaapefficientie: value })
                                        }
                                        onDoValidate={async () => {
                                            await props.validateOneCustomControl('slechteSlaapefficientie');
                                        }}
                                        text="Niet optimale slaapefficiëntie"
                                    />
                                    <InputCheckbox
                                        automation="afwijkendHartritme"
                                        checked={customControlData.afwijkendHartritme}
                                        error={props.errors.get('customControlData.afwijkendHartritme')}
                                        onChange={async (value) => {
                                            const update: Partial<IOsasvcV2CustomControlData> = {};
                                            update.afwijkendHartritme = value;
                                            if (!value) {
                                                update.afwijkendHartritmeRemarks = null;
                                            } else {
                                                update.afwijkendHartritmeRemarks =
                                                    'Bericht aan huisarts: Overweeg holteronderzoek voor deze patiënt';
                                            }
                                            await props.handleChangeCustomControl(update);
                                        }}
                                        onDoValidate={async () =>
                                            await props.validateOneCustomControl('afwijkendHartritme')
                                        }
                                        text="Afwijkend hartritme"
                                    />
                                    {customControlData.afwijkendHartritme && (
                                        <NullableInputText
                                            automation="afwijkendHartritmeRemarks"
                                            error={props.errors.get('customControlData.afwijkendHartritmeRemarks')}
                                            onChange={async (value) =>
                                                await props.handleChangeCustomControl({
                                                    afwijkendHartritmeRemarks: value,
                                                })
                                            }
                                            onDoValidate={async () =>
                                                await props.validateOneCustomControl('afwijkendHartritmeRemarks')
                                            }
                                            placeholder="Namelijk..."
                                            value={customControlData.afwijkendHartritmeRemarks}
                                        />
                                    )}
                                    <InputTextCheckbox
                                        automation="overigeRelevanteBevindingenAndersText"
                                        error={props.errors.get(
                                            'customControlData.overigeRelevanteBevindingenAndersText'
                                        )}
                                        expandableTextArea={true}
                                        labelStyle={{ width: '100%' }}
                                        onChange={async (value, andersChecked) => {
                                            await props.handleChangeCustomControl({
                                                overigeRelevanteBevindingenAndersText: value,
                                            });
                                        }}
                                        onDoValidate={async () => {
                                            await props.validateOneCustomControl(
                                                'overigeRelevanteBevindingenAndersText'
                                            );
                                        }}
                                        placeholder="Anders, namelijk..."
                                        text={customControlData.overigeRelevanteBevindingenAndersText}
                                    />
                                </InputContainer>
                            </HorizontalFormGroup>
                        </>
                    )}

                    <HeaderDivider text={translateKey('common.conclusion')} />
                    <TextAreaField
                        error={props.errors.get('customControlData.conclusie')}
                        fieldKey="conclusie"
                        isRequired
                        label={translateKey('common.conclusion')}
                        placeholder="Omschrijf je werkdiagnose en mogelijke differentiaaldiagnoses.."
                    />

                    <AssetDropzoneWithActions
                        acceptedFiles={contactFileExtensions}
                        assets={props.request.assets}
                        assetsBaseUrl={FilesUrlBase.requests}
                        cleanDropzoneAfterCompletingFiles={props.isAutoSaveActive}
                        onChange={async (assets) => await props.handleChange('assets', assets)}
                        onUploading={async (isUploading) => setButtonDisabled(isUploading)}
                        processActivityId={props.request.id}
                        service={props.service}
                        showAdditionalFilesCheckbox={props.previousRequest !== undefined}
                        updateNewRequestPreviousRequest={props.updateNewRequestPreviousRequest}
                    />

                    <HeaderDivider text="Therapie beleid" />
                    <FormGroup
                        hasErrors={props.errors.has('customControlData.therapie_gestart')}
                        isRequired
                        label="Therapie gestart"
                    >
                        <RadioGroup
                            layout="row"
                            name="therapie_gestart"
                            onChange={async (event) => {
                                await props.handleChangeCustomControl({
                                    therapie_gestart: event.target.value === 'true',
                                    therapie_gestart_namelijk: null,
                                    therapie: null,
                                    betreft_proefperiode: null,
                                });
                                await props.validateOneCustomControl('therapie_gestart');
                            }}
                            sx={radioGroupStyles}
                            value={customControlData.therapie_gestart?.toString() ?? ''}
                        >
                            <RadioButton
                                label="Ja"
                                value="true"
                            />
                            <RadioButton
                                label="Nee"
                                value="false"
                            />
                        </RadioGroup>
                    </FormGroup>

                    {customControlData.therapie_gestart && (
                        <>
                            <HorizontalFormGroup
                                hasErrors={props.errors.has('customControlData.therapie_gestart_namelijk')}
                            >
                                <FormLabel isRequired>Namelijk</FormLabel>
                                <RadioGroup
                                    name="therapie_gestart_namelijk"
                                    onChange={async (event) => {
                                        await props.handleChangeCustomControl({
                                            therapie_gestart_namelijk: event.target.value,
                                        });
                                        await props.validateOneCustomControl('therapie_gestart_namelijk');
                                    }}
                                    value={customControlData.therapie_gestart_namelijk ?? ''}
                                >
                                    <Radio
                                        label={OsasvcV2TherapieNamelijkOption.spt}
                                        value={OsasvcV2TherapieNamelijkOption.spt}
                                    />
                                    <Radio
                                        label={OsasvcV2TherapieNamelijkOption.mra}
                                        value={OsasvcV2TherapieNamelijkOption.mra}
                                    />
                                    <Radio
                                        label={OsasvcV2TherapieNamelijkOption.cpap}
                                        value={OsasvcV2TherapieNamelijkOption.cpap}
                                    />
                                    <Radio
                                        label={OsasvcV2TherapieNamelijkOption.biPap}
                                        value={OsasvcV2TherapieNamelijkOption.biPap}
                                    />
                                    <Feedback error={props.errors.get('customControlData.therapie_gestart_namelijk')} />
                                </RadioGroup>
                            </HorizontalFormGroup>

                            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.therapie')}>
                                <FormLabel isRequired>Therapie</FormLabel>
                                <RadioGroup
                                    name="therapie"
                                    onChange={async (event) => {
                                        await props.handleChangeCustomControl({
                                            therapie: event.target.value,
                                        });
                                        await props.validateOneCustomControl('therapie');
                                    }}
                                    value={customControlData.therapie ?? ''}
                                >
                                    <Radio
                                        label={OsasvcV2CTherapieGestartOption.continue}
                                        value={OsasvcV2CTherapieGestartOption.continue}
                                    />
                                    <Radio
                                        label={OsasvcV2CTherapieGestartOption.stop}
                                        value={OsasvcV2CTherapieGestartOption.stop}
                                    />
                                    <Feedback error={props.errors.get('customControlData.therapie')} />
                                </RadioGroup>
                            </HorizontalFormGroup>

                            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.betreft_proefperiode')}>
                                <FormLabel isRequired>Betreft proefperiode</FormLabel>
                                <RadioGroup
                                    name="betreft_proefperiode"
                                    onChange={async (event) => {
                                        await props.handleChangeCustomControl({
                                            betreft_proefperiode: event.target.value === 'true',
                                        });
                                        await props.validateOneCustomControl('betreft_proefperiode');
                                    }}
                                    value={
                                        customControlData.betreft_proefperiode !== null
                                            ? customControlData.betreft_proefperiode.toString()
                                            : ''
                                    }
                                >
                                    <Radio
                                        label="Ja, de proefperiode wordt beëindigd"
                                        value={'true'}
                                    />
                                    <Radio
                                        label="Nee"
                                        value={'false'}
                                    />
                                    <Feedback error={props.errors.get('customControlData.betreft_proefperiode')} />
                                </RadioGroup>
                            </HorizontalFormGroup>
                        </>
                    )}

                    <HeaderDivider text="Nieuwe therapie starten" />
                    <FormGroup label="Nieuwe therapie starten">
                        <RadioGroup
                            layout="row"
                            name="nieuwe_therapie_starten"
                            onChange={async (event) => {
                                const defaultNumberOfWeeks =
                                    event.target.value === 'true' &&
                                    !props.request.customControlData.osasq_resend_week_dif &&
                                    !activeOsasqResendDate
                                        ? 6
                                        : props.request.customControlData.osasq_resend_week_dif;
                                await props.handleChangeCustomControl({
                                    nieuwe_therapie_starten: event.target.value === 'true',
                                    nieuwe_therapie_starten_namelijk: null,
                                    cpap_aangepaste_instellingen_namelijk: null,
                                    nieuwe_therapie_starten_bipap_instellingen: null,
                                    nieuwe_therapie_starten_toelichting: null,
                                    nieuwe_therapie_starten_mra_abcd_score: null,
                                    cpap_instellingen: null,
                                    vervolg_afspraak_met_specialist: {
                                        volgens_protocol: false,
                                        anders: null,
                                    },
                                    vervolg_afspraak_met_specialist_nieuw_slaaponderzoek: null,
                                    osasq_resend_week_dif: defaultNumberOfWeeks,
                                    osasq_resend_date: defaultNumberOfWeeks
                                        ? DateTime.now().add(defaultNumberOfWeeks, 'weeks').format('YYYY-MM-DD')
                                        : null,
                                });
                                await props.validateOneCustomControl('nieuwe_therapie_starten');
                            }}
                            sx={radioGroupStyles}
                            value={customControlData.nieuwe_therapie_starten?.toString() ?? ''}
                        >
                            <RadioButton
                                label="Ja"
                                value="true"
                            />
                            <RadioButton
                                label="Nee"
                                value="false"
                            />
                        </RadioGroup>
                    </FormGroup>

                    {customControlData.nieuwe_therapie_starten && (
                        <>
                            <HorizontalFormGroup
                                hasErrors={props.errors.has('customControlData.nieuwe_therapie_starten_namelijk')}
                            >
                                <FormLabel isRequired>Namelijk</FormLabel>
                                <RadioGroup
                                    name="nieuwe_therapie_starten_namelijk"
                                    onChange={async (event) => {
                                        const updateInfo = {
                                            ...props.request.customControlData,
                                            nieuwe_therapie_starten_namelijk: event.target.value,
                                            cpap_instellingen:
                                                event.target.value === OsasvcV2TherapieNamelijkOption.cpap
                                                    ? OsasvcV2CpapOption.standard
                                                    : null,
                                            nieuwe_therapie_starten_mra_abcd_score: null,
                                            cpap_aangepaste_instellingen_namelijk: null,
                                            nieuwe_therapie_starten_bipap_instellingen: null,
                                        };
                                        const defaultNumberOfWeeks = getOsasqWeeksDefaultTiming(
                                            event.target.value as OsasvcV2TherapieNamelijkOption
                                        );
                                        if (defaultNumberOfWeeks) {
                                            updateInfo.osasq_resend_week_dif = defaultNumberOfWeeks;
                                            updateInfo.osasq_resend_date = DateTime.now()
                                                .add(defaultNumberOfWeeks, 'weeks')
                                                .format('YYYY-MM-DD');
                                        }
                                        await props.handleChangeCustomControl({ ...updateInfo });
                                        await props.validateOneCustomControl('nieuwe_therapie_starten_namelijk');
                                    }}
                                    value={customControlData.nieuwe_therapie_starten_namelijk ?? ''}
                                >
                                    <Radio
                                        label={OsasvcV2TherapieNamelijkOption.spt}
                                        value={OsasvcV2TherapieNamelijkOption.spt}
                                    />
                                    <Radio
                                        label={OsasvcV2TherapieNamelijkOption.mra}
                                        value={OsasvcV2TherapieNamelijkOption.mra}
                                    />
                                    <>
                                        {customControlData.nieuwe_therapie_starten_namelijk ===
                                            OsasvcV2TherapieNamelijkOption.mra && (
                                            <HorizontalFormGroup
                                                hasErrors={props.errors.has(
                                                    'customControlData.nieuwe_therapie_starten_mra_abcd_score'
                                                )}
                                            >
                                                <RadioGroup
                                                    name="nieuwe_therapie_starten_mra_abcd_score"
                                                    onChange={async (event) => {
                                                        await props.handleChangeCustomControl({
                                                            nieuwe_therapie_starten_mra_abcd_score: event.target.value,
                                                        });

                                                        await props.validateOneCustomControl(
                                                            'nieuwe_therapie_starten_mra_abcd_score'
                                                        );
                                                    }}
                                                    style={{ paddingLeft: '36px' }}
                                                    value={
                                                        customControlData.nieuwe_therapie_starten_mra_abcd_score ?? ''
                                                    }
                                                >
                                                    <Radio
                                                        label={MraAbcdScore.A}
                                                        value={MraAbcdScore.A}
                                                    />
                                                    <Radio
                                                        label={MraAbcdScore.B}
                                                        value={MraAbcdScore.B}
                                                    />
                                                    <Radio
                                                        label={MraAbcdScore.C}
                                                        value={MraAbcdScore.C}
                                                    />
                                                    <Radio
                                                        label={MraAbcdScore.D}
                                                        value={MraAbcdScore.D}
                                                    />
                                                    <Feedback
                                                        error={props.errors.get(
                                                            'customControlData.nieuwe_therapie_starten_mra_abcd_score'
                                                        )}
                                                    />
                                                </RadioGroup>
                                            </HorizontalFormGroup>
                                        )}
                                    </>
                                    <Radio
                                        label={OsasvcV2TherapieNamelijkOption.cpap}
                                        value={OsasvcV2TherapieNamelijkOption.cpap}
                                    />
                                    <>
                                        {customControlData.nieuwe_therapie_starten_namelijk ===
                                            OsasvcV2TherapieNamelijkOption.cpap && (
                                            <RadioGroup
                                                name="cpapInstellingen"
                                                onChange={async (event) =>
                                                    await props.handleChangeCustomControl({
                                                        cpap_instellingen: event.target.value,
                                                        cpap_aangepaste_instellingen_namelijk: null,
                                                    })
                                                }
                                                style={{ paddingLeft: '36px' }}
                                                value={customControlData.cpap_instellingen ?? ''}
                                            >
                                                <Radio
                                                    label={OsasvcV2CpapOption.standard}
                                                    value={OsasvcV2CpapOption.standard}
                                                />
                                                <Radio
                                                    label={OsasvcV2CpapOption.custom}
                                                    value={OsasvcV2CpapOption.custom}
                                                />
                                                <>
                                                    {customControlData.cpap_instellingen ===
                                                        OsasvcV2CpapOption.custom && (
                                                        <TextAreaField
                                                            error={props.errors.get(
                                                                'customControlData.cpap_aangepaste_instellingen_namelijk'
                                                            )}
                                                            fieldKey="cpap_aangepaste_instellingen_namelijk"
                                                            label={''}
                                                            placeholder="Namelijk..."
                                                        />
                                                    )}
                                                </>
                                            </RadioGroup>
                                        )}
                                    </>
                                    <Radio
                                        label={OsasvcV2TherapieNamelijkOption.biPap}
                                        value={OsasvcV2TherapieNamelijkOption.biPap}
                                    />
                                    <>
                                        {customControlData.nieuwe_therapie_starten_namelijk ===
                                            OsasvcV2TherapieNamelijkOption.biPap && (
                                            <TextAreaField
                                                error={props.errors.get(
                                                    'customControlData.nieuwe_therapie_starten_bipap_instellingen'
                                                )}
                                                fieldKey="nieuwe_therapie_starten_bipap_instellingen"
                                                label={''}
                                                placeholder="Instellingen..."
                                            />
                                        )}
                                    </>

                                    <Feedback
                                        error={props.errors.get('customControlData.nieuwe_therapie_starten_namelijk')}
                                    />
                                </RadioGroup>
                            </HorizontalFormGroup>

                            <TextAreaField
                                error={props.errors.get('customControlData.nieuwe_therapie_starten_toelichting')}
                                fieldKey="nieuwe_therapie_starten_toelichting"
                                label="Toelichting"
                                placeholder="Een toelichting of opmerkingen..."
                            />
                        </>
                    )}

                    <HeaderDivider text="Aanvullende afspraken" />
                    <HorizontalFormGroup>
                        <FormLabel>Aanvullend beleid</FormLabel>
                        <InputContainer>
                            <InputCheckbox
                                checked={customControlData.aanvullend_beleid.advies_lifestyle}
                                onChange={async (value) =>
                                    await handleChangeAanvullendBeleid('advies_lifestyle', value)
                                }
                                text="Advies lifestyle"
                            />

                            {customControlData.aanvullend_beleid.advies_lifestyle && (
                                <NullableInputText
                                    automation="adviesLifestyleVeranderingRemarks"
                                    onChange={async (value) =>
                                        await handleChangeAanvullendBeleid('advies_lifestyle_namelijk', value)
                                    }
                                    onDoValidate={async () =>
                                        await props.validateOneCustomControl(
                                            'aanvullend_beleid.advies_lifestyle_namelijk'
                                        )
                                    }
                                    placeholder="Namelijk..."
                                    value={customControlData.aanvullend_beleid.advies_lifestyle_namelijk}
                                />
                            )}

                            <InputTextCheckbox
                                automation="beleidAndersText"
                                error={props.errors.get('customControlData.aanvullend_beleid.anders')}
                                expandableTextArea
                                onChange={async (value) => await handleChangeAanvullendBeleid('anders', value)}
                                onDoValidate={async () => {
                                    await props.validateOneCustomControl('aanvullend_beleid.anders');
                                }}
                                placeholder="Anders, namelijk..."
                                text={customControlData.aanvullend_beleid.anders}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    {therapielandProgrammaIsModalShown && (
                        <TherapielandProgrammasModal
                            checkedTreatments={props.request.customControlData.checkedTreatments}
                            handleChange={async (values: ICheckedTreatment[]) => {
                                setTherapielandProgrammaIsModalShown(false);
                                await props.handleChangeCustomControl({ checkedTreatments: values });
                            }}
                            handleClose={async () => {
                                setTherapielandProgrammaIsModalShown(false);
                            }}
                            isOpen={therapielandProgrammaIsModalShown}
                            patientId={props.request.patientId}
                        />
                    )}

                    <HorizontalFormGroup>
                        <FormLabel>E-health programma´s</FormLabel>
                        <InputContainer>
                            {props.request.customControlData.checkedTreatments?.map((treatment: ICheckedTreatment) => {
                                return (
                                    <FormParagraph bold>
                                        {treatment.treatmentName + ' '}
                                        <IconOnClick
                                            icon={'delete'}
                                            onClick={async () => {
                                                const updatedTreatments =
                                                    props.request.customControlData.checkedTreatments.filter(
                                                        (checkedTreatment: ICheckedTreatment) =>
                                                            checkedTreatment.id !== treatment.id
                                                    );
                                                await props.handleChangeCustomControl({
                                                    checkedTreatments: updatedTreatments,
                                                });
                                            }}
                                        />
                                    </FormParagraph>
                                );
                            })}

                            <ButtonContainer
                                buttons={[
                                    <CheckMarkButton
                                        disabled={!hasActiveGgzbTherServiceConsumerRights}
                                        isChecked={false}
                                        onClick={() => setTherapielandProgrammaIsModalShown(true)}
                                    >
                                        {translateKey('diagnosticReport.selectTherapyLandProgramme')}
                                    </CheckMarkButton>,
                                ]}
                                noMargin
                                wrapContent
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    {!!props.request.customControlData.checkedTreatments?.length && (
                        <Alert appearance="warning">Controleer goed. Klopt het e-mailadres van de patiënt?</Alert>
                    )}

                    {!hasActiveGgzbTherServiceConsumerRights && (
                        <Alert appearance="warning">
                            Je hebt geen rechten voor het aanvragen van de programma´s. Neem contact op met de
                            servicedesk.
                        </Alert>
                    )}

                    {!!props.request.customControlData.checkedTreatments?.length && (
                        <HorizontalFormGroup
                            hasErrors={props.errors.has('customControlData.emailAddress') || showEmailAddressError}
                        >
                            <FormLabel isRequired>{translateKey('common.emailAddress')}</FormLabel>
                            <InputContainer>
                                <TextInput
                                    error={showEmailError(
                                        showEmailAddressError,
                                        props.errors.get('customControlData.emailAddress')
                                    )}
                                    onBlur={async () => {
                                        await props.validateOneCustomControl('emailAddress');
                                        if (
                                            await isEmailDuplicateForPatient(
                                                props.request.customControlData.emailAddress,
                                                props.request.patientId,
                                                abortSignal
                                            )
                                        ) {
                                            setShowEmailAddressError(true);
                                        }
                                    }}
                                    onChange={async ({ target: { value } }) => {
                                        setShowEmailAddressError(false);
                                        await props.handleChangeCustomControl({ emailAddress: value });
                                    }}
                                    placeholder={translateKey('diagnosticReport.patientEmailAddress')}
                                    value={props.request.customControlData.emailAddress || ''}
                                />
                            </InputContainer>
                        </HorizontalFormGroup>
                    )}

                    <HorizontalFormGroup>
                        <FormLabel>Vervolgafspraak met specialist</FormLabel>
                        {customControlData.nieuwe_therapie_starten && (
                            <InputContainer>
                                <InputCheckbox
                                    checked={customControlData.vervolg_afspraak_met_specialist.volgens_protocol}
                                    onChange={async (value) =>
                                        await handleChangeVervolgAfspraakMetSpecialist('volgens_protocol', value)
                                    }
                                    text="Volgens protocol"
                                />

                                <InputTextCheckbox
                                    automation="vervolgAfspraakMetSpecialistAndersText"
                                    error={props.errors.get('customControlData.vervolg_afspraak_met_specialist.anders')}
                                    expandableTextArea
                                    onChange={async (value) =>
                                        await handleChangeVervolgAfspraakMetSpecialist('anders', value)
                                    }
                                    onDoValidate={async () => {
                                        await props.validateOneCustomControl('vervolg_afspraak_met_specialist.anders');
                                    }}
                                    placeholder="Anders, namelijk..."
                                    text={customControlData.vervolg_afspraak_met_specialist.anders}
                                />
                            </InputContainer>
                        )}
                        {!customControlData.nieuwe_therapie_starten && (
                            <InputContainer>
                                <NullableInputText
                                    automation="vervolgafspraakMetSpecialist"
                                    error={props.errors.get(
                                        'customControlData.vervolg_afspraak_met_specialist_nieuw_slaaponderzoek'
                                    )}
                                    onChange={async (value) =>
                                        await props.handleChangeCustomControl({
                                            vervolg_afspraak_met_specialist_nieuw_slaaponderzoek: value,
                                        })
                                    }
                                    onDoValidate={async () =>
                                        await props.validateOneCustomControl(
                                            'vervolg_afspraak_met_specialist_nieuw_slaaponderzoek'
                                        )
                                    }
                                    placeholder="Beschrijf de vervolgafspraak..."
                                    value={customControlData.vervolg_afspraak_met_specialist_nieuw_slaaponderzoek}
                                />
                            </InputContainer>
                        )}
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={props.errors.has('customControlData.osasq_resend_week_dif')}>
                        <FormLabel>Herzenden vragenlijsten</FormLabel>
                        <InputContainer size="auto">
                            <CheckboxInput
                                automation="osasqResendDateCheckbox"
                                checked={!!props.request.customControlData.osasq_resend_week_dif}
                                disabled={!!activeOsasqResendDate}
                                onChange={async (value) => {
                                    await props.handleChangeCustomControl({
                                        osasq_resend_week_dif: value ? 6 : null,
                                        osasq_resend_date: value
                                            ? DateTime.now().add(6, 'weeks').format('YYYY-MM-DD')
                                            : null,
                                    });
                                }}
                                onDoValidate={async () => {
                                    await props.validateOneCustomControl('osasq_resend_week_dif');
                                }}
                                text="Ja, na"
                            />
                        </InputContainer>
                        <InputContainer className="col-sm-12 col-xl-6">
                            <NumberInput
                                disabled={!!activeOsasqResendDate}
                                error={props.errors.get('customControlData.osasq_resend_week_dif')}
                                fieldKey="osasq_resend_week_dif"
                                onChange={async (value) => {
                                    await props.handleChangeCustomControl({
                                        osasq_resend_week_dif: value,
                                        osasq_resend_date: value
                                            ? DateTime.now().add(value, 'weeks').format('YYYY-MM-DD')
                                            : null,
                                    });
                                }}
                                onDoValidate={async () => {
                                    await props.validateOneCustomControl('osasq_resend_week_dif');
                                }}
                                placeholder=""
                                text={`${
                                    props.request.customControlData.osasq_resend_week_dif === 1 ? 'week' : 'weken'
                                } ${
                                    props.request.customControlData.osasq_resend_week_dif
                                        ? `(${DateTime.now()
                                              .add(props.request.customControlData.osasq_resend_week_dif, 'weeks')
                                              .format('D MMMM YYYY')})`
                                        : ''
                                }`}
                                textStyle={activeOsasqResendDate ? { color: '#A69A91' } : undefined}
                                value={props.request.customControlData.osasq_resend_week_dif}
                                width="small"
                            />
                        </InputContainer>
                        {activeOsasqResendDate && (
                            <Alert appearance="warning">
                                Er staan al vragenlijsten ingepland voor deze patiënt (
                                {DateTime.from(activeOsasqResendDate).format('DD-MM-YYYY')}), als je toch een ander
                                moment in wilt plannen kan dit in het zorgpad van de patiënt.
                            </Alert>
                        )}
                    </HorizontalFormGroup>

                    <TextAreaField
                        automation="adviesAanDeHuisarts"
                        error={props.errors.get('customControlData.advies_aan_de_huisarts')}
                        fieldKey="advies_aan_de_huisarts"
                        label={'Advies aan de huisarts'}
                        placeholder="Het advies..."
                    />

                    <TextAreaField
                        automation="remark"
                        error={props.errors.get('customControlData.toelichting')}
                        fieldKey="toelichting"
                        label={'Toelichting'}
                        placeholder="Een toelichting of opmerkingen..."
                    />
                </FormContextProvider>

                <Modal
                    actionButtons={[
                        <Button
                            color="secondary"
                            onClick={() => {
                                setShowModal(false);
                                navigate(-1);
                            }}
                        >
                            {'Ok'}
                        </Button>,
                    ]}
                    automation="cannotContinueModal"
                    isOpen={showModal}
                    onClose={() => {
                        setShowModal(false);
                        navigate(-1);
                    }}
                    size="small"
                    title={'Vervolgconsult kan niet worden gestart'}
                >
                    <Text>{modalMessage}</Text>
                </Modal>

                <ButtonContainer
                    alignRight
                    buttons={getButtons()}
                />
            </Form>
        </NewOrEditRequestProcessContainer>
    );

    async function loadPatientQuestionnaires() {
        const previousSomnologyRequests = await abortableFetch.fetchJson(
            `/api/requestProcesses?patientId=${props.request.patientId}&episodeCategory=${EpisodeCategory.somnology}&orderByCreatedAtDesc=true`,
            'GET',
            undefined,
            [],
            [400]
        );
        const osasExaminations = previousSomnologyRequests.json.items.filter(
            (item: { code: ServiceCode; status: RequestStatus }) =>
                [ServiceCode.OSASO, ServiceCode.OSASVO, ServiceCode.OSASQ].includes(item.code)
        );

        const allCompletedOsasPatientQuestionnaires: IOsasvcQuestionnaire[] = [];

        for (const osasExamination of osasExaminations) {
            allCompletedOsasPatientQuestionnaires.push(...(await loadQuestionnairesFromProcedure(osasExamination.id)));
        }

        const last6Fosq10Questionanires = allCompletedOsasPatientQuestionnaires
            .filter((questionnaire) => questionnaire.name === OsasQuestionnaireName.fosq10)
            .sort((questionnaire1, questionnaire2) => (questionnaire2.dateSent > questionnaire1.dateSent ? 1 : -1))
            .slice(0, 6);

        const last6EssQuestionanires = allCompletedOsasPatientQuestionnaires
            .filter((questionnaire) => questionnaire.name === OsasQuestionnaireName.ess)
            .sort((questionnaire1, questionnaire2) => (questionnaire2.dateSent > questionnaire1.dateSent ? 1 : -1))
            .slice(0, 6);

        await props.handleChangeCustomControl({
            questionnaires: [...last6Fosq10Questionanires, ...last6EssQuestionanires],
        });
    }

    async function loadPatientEmail() {
        const result = await fetchJson(`/api/patients/${props.request.patientId}`, 'GET');
        await props.handleChangeCustomControl({ emailAddress: result.json.emailAddress });
    }

    async function loadQuestionnairesFromProcedure(requestId: string) {
        const customControlDataResult = await abortableFetch.fetchJson(
            `/api/requests/${requestId}/procedure`,
            'GET',
            undefined,
            [],
            [400, 404]
        );

        const requestQuestionnaires = customControlDataResult.json?.customControlData?.questionnaires ?? [];

        if (requestQuestionnaires.length === 0) {
            return requestQuestionnaires;
        }

        const allPatientQuestionnairesCompleted = requestQuestionnaires
            .filter(
                (questionnaire: any) =>
                    [OsasQuestionnaireName.ess, OsasQuestionnaireName.fosq10].includes(questionnaire.name) &&
                    Object.values(questionnaire.answer).every((value) => value !== null) &&
                    questionnaire.dateSent
            )
            .map((questionnaire: any) => {
                return {
                    name: questionnaire.name,
                    score: getOsasQuestionnaireScore(questionnaire),
                    dateSent: questionnaire.dateSent,
                };
            });

        return allPatientQuestionnairesCompleted;
    }

    async function handleChangeNieuwSlaaponderzoek(value: boolean) {
        await props.handleChangeCustomControl({
            nieuw_slaaponderzoek: value,
            nieuw_slaaponderzoek_start_date: null,
            nieuw_slaaponderzoek_end_date: null,
            gemiddeldeAhi: null,
            gemiddeldeOdi: null,
            odi4: null,
            gemiddeldeSaturatie: null,
            laagstGemetenSaturatie: null,
            relevanteHypoxemie: null,
            relevanteHypoxemieRemarks: null,
            positieafhankelijk: null,
            positieafhankelijkRemarks: null,
            inefficienteSlaaphygiene: false,
            inefficienteSlaaphygieneRemarks: null,
            veelBewegingen: false,
            slechteSlaapefficientie: false,
            afwijkendHartritme: false,
            afwijkendHartritmeRemarks: null,
            overigeRelevanteBevindingenAndersText: null,
            vervolg_afspraak_met_specialist: {
                volgens_protocol: false,
                anders: null,
            },
            vervolg_afspraak_met_specialist_nieuw_slaaponderzoek: null,
        });
    }

    async function checkForCompletedRequest() {
        setButtonDisabled(true);

        const { isOsasoCompleted, isOsasc2Completed, isOsasvoCompleted } = await canCreateOsasvc(
            props.request.patientId
        );

        const canCreateConsult = (isOsasoCompleted && isOsasc2Completed) || isOsasvoCompleted;
        setShowModal(!canCreateConsult);
        setButtonDisabled(!canCreateConsult);
        setCanCreateRequestOsasvc(canCreateConsult);
        setModalMessage(
            !isOsasoCompleted
                ? translateKey('osasPermission.cantCreateOsasvcOsasoMissing')
                : translateKey('osasPermission.cantCreateOsasvcOsasc2Missing')
        );
    }

    async function initializeMissingCustomControlDataFields() {
        const initializedCustomControlData = osasvcV2RequestInitializeCustomControlData();

        await props.handleChangeCustomControl({
            ...initializedCustomControlData,
            ...props.request.customControlData,
            aanvullend_beleid: {
                ...initializedCustomControlData.aanvullend_beleid,
                ...props.request.customControlData.aanvullend_beleid,
            },
            toesturen_therapieland_modules: {
                ...initializedCustomControlData.toesturen_therapieland_modules,
                ...props.request.customControlData.toesturen_therapieland_modules,
            },
            vervolg_afspraak_met_specialist: {
                ...initializedCustomControlData.vervolg_afspraak_met_specialist,
                ...props.request.customControlData.vervolg_afspraak_met_specialist,
            },
        });
    }

    async function validateAndSaveRequest(saveType: 'final' | 'draft') {
        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setButtonDisabled(false);

            return;
        }

        if (showEmailAddressError) {
            setButtonDisabled(false);

            return;
        }

        const requestId = await props.onSaveRequest(saveType);
        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'request',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }
        await props.onSave(requestId);
        if (props.request.customControlData.osasq_resend_date && props.reloadProcess) {
            await props.reloadProcess(ReloadEpisode.reloadAll);
        }
    }

    function getValidationErrorsForNieuwSlaaponderzoekDates() {
        if (
            customControlData.nieuw_slaaponderzoek_start_date !== null &&
            customControlData.nieuw_slaaponderzoek_end_date === null
        ) {
            return 'koi_date';
        } else if (
            customControlData.nieuw_slaaponderzoek_start_date &&
            customControlData.nieuw_slaaponderzoek_end_date &&
            DateTime.from(customControlData.nieuw_slaaponderzoek_start_date).isSameOrAfter(
                customControlData.nieuw_slaaponderzoek_end_date
            )
        ) {
            return 'koi_endDate';
        } else {
            return '';
        }
    }

    function getButtons() {
        const buttons = [
            <CancelButton
                cancel={props.onCancel}
                entity={ProcessEntity.request}
                isExisting={!!props.request.id}
                revertChanges={props.revertRequestChanges}
                serviceCode={props.service.code}
                serviceType={props.service.serviceType}
            />,
        ];

        if (canCreateRequestOsasvc) {
            buttons.push(
                <SaveButton
                    buttonDisabled={buttonDisabled}
                    save={async () => {
                        await validateAndSaveRequest('draft');
                    }}
                />,
                <SaveSendButton
                    buttonDisabled={buttonDisabled || !hasActiveGgzbTherServiceConsumerRights}
                    saveSend={async () => {
                        await validateAndSaveRequest('final');
                    }}
                />
            );
        }

        return buttons;
    }

    async function handleChangeAanvullendBeleid<K extends keyof IAanvullendBeleid>(
        name: K,
        value: IAanvullendBeleid[K]
    ) {
        const aanvullendBeleidUpdate: IAanvullendBeleid = {
            ...props.request.customControlData.aanvullend_beleid,
            [name]: value,
        };

        if (name === 'advies_lifestyle') {
            aanvullendBeleidUpdate.advies_lifestyle_namelijk = null;
        }

        await props.handleChangeCustomControl({
            aanvullend_beleid: { ...aanvullendBeleidUpdate },
        });
    }

    async function handleChangeVervolgAfspraakMetSpecialist<K extends keyof IVervolgAfspraakMetSpecialist>(
        name: K,
        value: IVervolgAfspraakMetSpecialist[K]
    ) {
        await props.handleChangeCustomControl({
            vervolg_afspraak_met_specialist: {
                ...props.request.customControlData.vervolg_afspraak_met_specialist,
                [name]: value,
            },
        });
    }

    function getOsasqWeeksDefaultTiming(nieuweTherapie: OsasvcV2TherapieNamelijkOption | null) {
        if (!activeOsasqResendDate && nieuweTherapie) {
            if (nieuweTherapie) {
                if (
                    [OsasvcV2TherapieNamelijkOption.cpap, OsasvcV2TherapieNamelijkOption.spt].includes(nieuweTherapie)
                ) {
                    return 6;
                }
                if (nieuweTherapie === OsasvcV2TherapieNamelijkOption.mra) {
                    return 12;
                }
            }
        }

        return null;
    }

    async function checkHasActiveOsasqRequest() {
        const result = await fetchActiveOsasqResendDate(patient.id);
        if (
            result &&
            (props.request.customControlData.osasq_resend_date || props.request.customControlData.osasq_resend_week_dif)
        ) {
            props.handleChangeCustomControl({ osasq_resend_date: null, osasq_resend_week_dif: null });
        }
        setActiveOsasqResendDate(result);
    }

    function showEmailError(isDuplicateEmail: boolean, validationEmailError?: string) {
        if (validationEmailError) {
            return getValidationErrorTranslation(validationEmailError);
        } else if (isDuplicateEmail) {
            return getValidationErrorTranslation('duplicatedEmailTherapieland');
        }

        return undefined;
    }

    async function isServiceConsumerOfGGZTherapieland() {
        const url = `/api/serviceConsumers?employmentId=${props.employmentId}&isActive=true`;
        const result = await fetchJson(url, 'GET');
        const hasServiceCodeGGZBther = result.json.items.some(
            (item: IServiceConsumer) => item.service.code === ServiceCode.GGZBther
        );
        setHasActiveGgzbTherServiceConsumerRights(hasServiceCodeGGZBther);
    }
}

export function osasvcV2RequestInitializeCustomControlData(): IOsasvcV2CustomControlData {
    return {
        date_contact: null,
        anamnese: null,
        onderzoek: null,
        questionnaires: [],
        conclusie: null,
        nieuw_slaaponderzoek: false,
        nieuw_slaaponderzoek_start_date: null,
        nieuw_slaaponderzoek_end_date: null,
        gemiddeldeAhi: null,
        gemiddeldeOdi: null,
        odi4: null,
        gemiddeldeSaturatie: null,
        laagstGemetenSaturatie: null,
        relevanteHypoxemie: null,
        relevanteHypoxemieRemarks: null,
        positieafhankelijk: null,
        positieafhankelijkRemarks: null,
        inefficienteSlaaphygiene: false,
        inefficienteSlaaphygieneRemarks: null,
        veelBewegingen: false,
        slechteSlaapefficientie: false,
        afwijkendHartritme: false,
        afwijkendHartritmeRemarks: null,
        overigeRelevanteBevindingenAndersText: null,
        therapie_gestart: null,
        therapie_gestart_namelijk: null,
        therapie: null,
        betreft_proefperiode: null,
        nieuwe_therapie_starten: null,
        cpap_instellingen: null,
        cpap_aangepaste_instellingen_namelijk: null,
        nieuwe_therapie_starten_namelijk: null,
        nieuwe_therapie_starten_bipap_instellingen: null,
        nieuwe_therapie_starten_toelichting: null,
        nieuwe_therapie_starten_mra_abcd_score: null,
        aanvullend_beleid: {
            advies_lifestyle: false,
            advies_lifestyle_namelijk: null,
            anders: null,
        },
        vervolg_afspraak_met_specialist: {
            volgens_protocol: false,
            anders: null,
        },
        vervolg_afspraak_met_specialist_nieuw_slaaponderzoek: null,
        advies_aan_de_huisarts: null,
        toelichting: null,
        osasq_resend_week_dif: null,
        osasq_resend_date: null,
        checkedTreatments: [],
        emailAddress: '',
    };
}
